/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ComponentItemFinderListItem, HousePlanFinderListItem } from '@gh/builder-shared';
import {
  CheckUsageDto,
  PbCcElementDto,
  PbCcStructureSaveDto,
  PbCostCenterDto,
  PbDefaultsDto,
  PbDetailsDto,
  PbDetailsSaveDto,
  PbDetailsSaveResponseDto,
  PbHousePlanDto,
  PbHousePlanSaveDto,
  PbHousePlansRequestDto,
  PriceBookListItemDto,
  PriceBookUsage,
} from '@gh/builder-shared-data';
import {
  bindUniqueId,
  DateTimeTypes,
  EntityConfig,
  entityValidatorByEntityConfig,
  EntityValidatorFn,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  NumberTypes,
  setEntityFields,
} from '@gh/common-shared';
import {
  DirtyType,
  EntityVersion,
  ID,
  WithCreatedDate,
  WithEntityState,
  WithRecalculationNeeded,
  WithVersion,
} from '@gh/common-shared-data';
import { WithOptimisticLock } from '@gh/core-data';
import { cast, currentDate } from '@gh/core-util';
import { assign, flatMap, omit, pick } from 'lodash';

export type PriceBookListItem = PriceBookListItemDto;
export type PriceBookListItemCheckUsage = CheckUsageDto;

export type PbMasterInfo = {
  name: string;
  usage?: PriceBookUsage;
  releaseDate: Date;
  repriceDate?: Date;
  withdrawalDate: Date;
};

export type PbDefaults = PbDefaultsDto;
export type PbHousePlanFilter = { isSalesEstimate: boolean; isQuickQuote: boolean };
export type PbHousePlan = WithEntityState<WithCreatedDate<WithRecalculationNeeded<PbHousePlanDto>>>;
export type PbCostCenter = PbCostCenterDto;
export type PbCcStructure = WithEntityState<WithCreatedDate<PbCcElementDto>>;
export type PbDetails = PbDetailsDto & { items?: PbCcStructure[] };
export type PbDetailsSaveResponse = WithVersion<WithOptimisticLock<{
  priceBookId?: ID;
  housePlans: PbHousePlan[];
  items: PbCcStructure[];
}>>;

export type PbHousePlanRequest = PbHousePlansRequestDto;

export type PbDetailsSave = WithOptimisticLock<WithVersion<{
  name: string;
  memo: string;
  usage: PriceBookUsage;
  defaults: PbDefaults;
  costCenters: PbCostCenter[];
  releaseDate: Date;
  withdrawalDate: Date;
  housePlans: PbHousePlan[];
  items: PbCcStructure[];
  isPersist: boolean;
  priceBookId?: ID;
}>>;

const PB_MASTER_INFO_ENTITY_CONFIG: EntityConfig<PbMasterInfo> = {
  name: { required: true },
  releaseDate: { required: true, type: DateTimeTypes.ShortDate },
  withdrawalDate: { required: true, type: DateTimeTypes.ShortDate },
  usage: { required: true },
};

export const validatePbMasterInfo =
  <EntityValidatorFn<PbMasterInfo>>entityValidatorByEntityConfig(PB_MASTER_INFO_ENTITY_CONFIG);

export const getPbHousePlanId = (hp: PbHousePlan) => hp.housePlanId;
export const getPbCostCenterId = (cc: PbCostCenter) => cc.costCenterId;
export const getPbCcStructureId = (structure: PbCcStructure) =>
  `${structure.costCenterId}:${structure.isComponent}:${structure.itemId}`;
export const getPbItemId = (struct: PbCcStructure) => struct.itemId;

export const pbHousePlanFromFinder = (hp: HousePlanFinderListItem, defaults: PbDefaults) => <PbHousePlan>({
  housePlanId: hp.housePlanId,
  code: hp.code,
  name: hp.name,
  cost: hp.cost,
  isQqAllowed: defaults.isQqAllowed,
  qqMarkupPct: defaults.qqMarkupPct,
  priceRoundUp: defaults.priceRoundUp,
  profitMarkupPct: defaults.profitMarkupPct,
  overheadMarkupPct: defaults.overheadMarkupPct,
  contingencyMarkupPct: defaults.contingencyMarkupPct,
  isManual: false,
  dirtyType: DirtyType.New,
  createdDate: currentDate(),
});

export const pbDetailsSaveToDto = (model: PbDetailsSave) => ({
  ...cast<PbDetailsSaveDto>(omit(model, 'housePlans', 'items', 'costCenters')),
  costCenters: model.costCenters.map(pbCostCenterToSaveDto),
  items: model.items.map(pbCcStructureToSaveDto),
  housePlans: model.housePlans.map(pbHousePlanToSaveDto),
});

export const pbDetailsFromDtoForClone = (model: PbDetails) => ({
  ...model,
  costCenters: model.costCenters.map((cc: PbCostCenterDto) => ({ ...cc, preloaded: true })),
});

export const pbCostCenterToSaveDto = ({ costCenterId, dirtyType }: PbCostCenter) => ({ costCenterId, dirtyType });

export const pbCcStructureToSaveDto = (structure: PbCcStructure) => <PbCcStructureSaveDto>pick(
  structure,
  'itemId',
  'costCenterId',
  'cost',
  'code',
  'price',
  'isComponent',
  'roundUp',
  'deletePct',
  'markupPct',
  'isSalesTax',
  'dirtyType',
);

export const pbHousePlanToRowDto = (hp: PbHousePlan) => <PbHousePlanSaveDto>assign(
  pick(
    hp,
    'isQqAllowed',
    'housePlanId',
    'cost',
    'isManual',
    'floorArea',
    'qqMarkupPct',
    'priceRoundUp',
    'profitMarkupPct',
    'overheadMarkupPct',
    'contingencyMarkupPct',
    'dirtyType',
    'createdDate',
  ));

export const pbHousePlanToSaveDto = (hp: PbHousePlan) => <PbHousePlanSaveDto>assign(
  { salesPriceManual: hp.isManual && hp.salesPrice || void 0 },
  pick(
    hp,
    'isQqAllowed',
    'housePlanId',
    'cost',
    'isManual',
    'floorArea',
    'qqMarkupPct',
    'priceRoundUp',
    'profitMarkupPct',
    'overheadMarkupPct',
    'contingencyMarkupPct',
    'dirtyType',
    'createdDate',
  ));

export const pbHousePlanToCalculateDto = (hp: PbHousePlan) => <PbHousePlanSaveDto>assign(
  { salesPriceManual: hp.isManual && hp.salesPrice || void 0 },
  <any>pick(
    hp,
    'housePlanId',
    'cost',
    'isManual',
    'floorArea',
    'qqMarkupPct',
    'priceRoundUp',
    'profitMarkupPct',
    'overheadMarkupPct',
    'contingencyMarkupPct',
    'isQq',
  ));

export const housePlanToRowPointer = (hp: PbHousePlan) => ({ rn: hp.rn - 1, name: hp.code });

export const pbDetailsSaveResponseFromDto = (response: PbDetailsSaveResponseDto) => ({
  priceBookId: response.priceBookId,
  housePlans: response.housePlans.map(pbHousePlanFromDto),
  items: response.items.map(pbCcStructureFromDto),
  version: response.version,
  etag: response.etag,
});

export const pbHousePlanFromDto = (dto: PbHousePlanDto) => ({
  ...dto,
  isRecalculationNeeded: false,
});

export const pbCcStructureFromDto = (dto: PbCcElementDto) => dto;

export const isCcNotPreloaded = (cc: PbCostCenter) => !cc.preloaded;

export type PbDetailsGetCostCenter = {
  priceBookId: ID;
  housePlanId: ID;
  costCenterId: ID;
  version: EntityVersion;
  code: string;
  name: string;
  cost: number;
  structures: PbCcStructure[];
  memo?: string;
};
export type PbDetailsGetItemsComponentsRequest = {
  priceBookId: ID;
  version?: EntityVersion;
  items: PbCcStructure[];
};
export type PbDetailsGetCostCenterRequest = {
  priceBookId: ID;
  costCenterId: ID;
  version?: EntityVersion;
  items: PbCcStructure[];
};

const PB_STRUCTURE_ENTITY_CONFIG: EntityConfig<PbCcStructure> = {
  roundUp: {
    required: true,
    editable: true,
    type: NumberTypes.RoundUp,
  },
  markupPct: {
    required: true,
    editable: true,
    type: NumberTypes.Percents_2,
  },
  deletePct: {
    required: true,
    editable: true,
    type: NumberTypes.Percents_2,
  },
};

export const validatePbStructureField = fieldValidatorByEntityConfig(PB_STRUCTURE_ENTITY_CONFIG);
export const pbStructureFieldState = fieldStateByEntityConfig(PB_STRUCTURE_ENTITY_CONFIG);

export function componentStructureFromFinder(structure: ComponentItemFinderListItem,
                                             defaults: PbDefaults,
                                             costCenterId: ID): PbCcStructure {

  return bindUniqueId({
    // @ts-ignore
    itemId: structure.id,
    // @ts-ignore
    costCenterId,
    ...<PbCcStructure>pick(structure, 'code', 'name', 'cost'),
    isActive: structure.active,
    quantity: 1,
    deletePct: defaults.deletePct,
    roundUp: structure.component ? defaults.componentRoundUp : defaults.itemRoundUp,
    markupPct: structure.component ? defaults.componentMarkupPct : defaults.itemMarkupPct,
    cost: structure.cost,
    isSalesTax: structure.isSalesTax,
    isComponent: structure.component,
    uomCode: structure.uomCode,
    createdDate: currentDate(),
  });
}

export const mergeDirtyHousePlan = (original: PbHousePlan, dirty?: PbHousePlan) =>
  dirty ? setEntityFields(original, dirty) : original;

export const pbDetailsGetCostCenterRequestToDto = (request: PbDetailsGetCostCenterRequest) => ({
  priceBookId: request.priceBookId,
  costCenterId: request.costCenterId,
  version: request.version,
  items: request.items.map(pbCcStructureToSaveDto),
});

export const pbDetailsToDtoForClone = (pbDetails: PbDetailsDto, name: string) => ({
  ...pbDetails,
  name,
  costCenters: pbDetails.costCenters.map((cc) => ({ ...cc, preloaded: true })),
  items: <PbCcStructure[]>flatMap(pbDetails.costCenters, (cc) => (cc.items || []).map((item) => ({
    ...item,
    costCenterId: cc.costCenterId,
  }))),
});
